<template>
  <div class="page-shell">
    <VendorDashboardHeader title="" subtitle="" />

    <div v-if="isLoading" class="fetching-state">
      <LoadingState :loading="isLoading" message="Loading inquiry details..." :spinner-size="24"
        :spinner-width="3" />
    </div>

    <p v-else-if="errorMessage" class="error-text">{{ errorMessage }}</p>
    <div v-else-if="!inquiryData" class="info-note">
      <v-icon size="18" class="info-note-icon">mdi-information-outline</v-icon>
      <p class="info-note-text">Inquiry details are unavailable.</p>
    </div>

    <template v-else>


      <InquiryDetailDescriptionSection :title="description.title" :time="description.time"
        :inquiry-no="description.inquiryNo" :description="description.description" :icon="description.icon"
        :icon-tone="description.iconTone" :priority="description.priority" />

      <InquiryDetailQuotesSection :quotes="quotes" :inquiry-id="inquiryData?.id" />


      <InquiryDetailDetailsSection :items="details" />

      <InquiryDetailAttachmentsSection v-if="attachments.length" :items="attachments" />

      <InquiryDetailResponsesSection v-if="responses.length && inquiryData?.type !== 'inspection'" :items="responses" />


    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import LoadingState from "@/shared/components/feedback/LoadingState.vue";
import VendorDashboardHeader from "@/modules/vendor/components/VendorDashboardHeader.vue";

import InquiryDetailAttachmentsSection from "@/modules/vendor/components/inquiry-detail/InquiryDetailAttachmentsSection.vue";
import InquiryDetailDescriptionSection from "@/modules/vendor/components/inquiry-detail/InquiryDetailDescriptionSection.vue";
import InquiryDetailDetailsSection from "@/modules/vendor/components/inquiry-detail/InquiryDetailDetailsSection.vue";
import InquiryDetailQuotesSection from "@/modules/vendor/components/inquiry-detail/InquiryDetailQuotesSection.vue";
import InquiryDetailResponsesSection from "@/modules/vendor/components/inquiry-detail/InquiryDetailResponsesSection.vue";

import {
  fetchVendorInquiryDetails,
  type InquiryDetail,
} from "@/shared/services/inquiries.api";
import { formatPhoneNumber } from "@/shared/utils/phone";


const route = useRoute();
const isLoading = ref(false);
const errorMessage = ref("");
const inquiryData = ref<InquiryDetail | null>(null);


function asString(value: unknown, fallback = "") {
  if (typeof value === "string" || typeof value === "number") return String(value);
  return fallback;
}

function formatLocation(source: InquiryDetail | null) {
  const area = source?.service_area;
  const city = asString(area?.city);
  const state = asString(area?.state);
  const zip = asString(area?.zip);
  return [city, state, zip].filter(Boolean).join(", ") || "-";
}

const timelineItems = computed(() => {
  const source = inquiryData.value;
  const timeline = source?.timeline || [];

  if (timeline.length) {
    return timeline.map((item, index) => ({
      title: asString(item.title, `Step ${index + 1}`),
      time: asString(item.time, "-"),
      state: item.state,
    }));
  }

  return [
    { title: "Inquiry Submitted", time: asString(source?.created_at, "-"), state: "done" },
    { title: "Vendors Notified", time: "-", state: "done" },
    { title: "Review In Progress", time: "-", state: "active" },
  ] as const;
});


const quotes = computed(() => {
  const items = inquiryData.value?.quotes || [];

  return items.map((item) => ({
    id: item.id,
    quoteNo: asString(item.quote_no),
    title: asString(item.vendor_name, "Quotation"),
    price: asString(item.price, "-"),
    date: asString(item.date, "-"),
    status: asString(item.status, "Received"),
    icon: "mdi-file-document-outline",
    iconTone: "quote-icon--peach",
  }));
});

const description = computed(() => {
  const source = inquiryData.value;
  const service = source?.service;
  const priority = asString(service?.priority);
  const inquiryType = asString(source?.type).toLowerCase();
  const serviceName =
    inquiryType === "inspection"
      ? "Inspection Request"
      : asString(service?.name, "Inquiry");
  const fallbackDescription = formatLocation(source);

  return {
    title: serviceName,
    time: asString(source?.created_at, "-"),
    inquiryNo: asString(source?.inquiry_no || source?.id, "-"),
    description: asString(
      service?.description,
      fallbackDescription || "No description available.",
    ),
    icon: "mdi-wrench",
    iconTone: "card-icon--peach",
    priority,
  };
});

const details = computed(() => {
  const source = inquiryData.value;
  const service = source?.service;
  const client = source?.client;
  const area = source?.service_area;
  const addressLine = asString(area?.address_line);
  const city = asString(area?.city);
  const state = asString(area?.state);
  const zip = asString(area?.zip);
  const locationParts = [addressLine, city, state, zip].filter(Boolean);
  const contactName = asString(client?.name);
  const contactPhoneRaw = asString(client?.phone);
  const contactPhone = contactPhoneRaw
    ? formatPhoneNumber(contactPhoneRaw)
    : "";
  const contactPerson = [contactName, contactPhone].filter(Boolean).join(" • ");

  return [
    {
      icon: "mdi-account-outline",
      label: "Name",
      value: contactName || "-",
    },
    {
      icon: "mdi-email-outline",
      label: "Email",
      value: asString(client?.email, "-"),
    },
    {
      icon: "mdi-phone-outline",
      label: "Phone",
      value: contactPhone || "-",
    },
    {
      icon: "mdi-map-marker-outline",
      label: "Service Address",
      value: locationParts.join(", ") || "-",
    },
    {
      icon: "mdi-clock-outline",
      label: "Submitted",
      value: asString(source?.created_at, "-"),
    },
  ];
});

const attachments = computed(() => {
  const items = inquiryData.value?.attachments || [];

  return items.map((item) => ({
    url: asString(item.url),
    description: asString(item.description, "Attachment"),
  }));
});

const responses = computed(() => {
  const items = inquiryData.value?.responses || [];

  return items.map((item) => ({
    label: asString(item.label, "Response"),
    value: asString(item.value, "-"),
  }));
});

async function loadInquiryDetail() {
  const id = asString(route.params.id).trim();
  if (!id) {
    errorMessage.value = "Inquiry not found.";
    inquiryData.value = null;
    return;
  }

  isLoading.value = true;
  errorMessage.value = "";

  try {
    const response = await fetchVendorInquiryDetails(id);
    inquiryData.value = response || null;
    if (!inquiryData.value) {
      errorMessage.value = "Inquiry details are unavailable.";
    }
  } catch (error) {
    inquiryData.value = null;
    errorMessage.value = "Unable to load inquiry details.";
    console.error("Failed to load inquiry details", error);
  } finally {
    isLoading.value = false;
  }
}

watch(
  () => route.params.id,
  () => {
    loadInquiryDetail();
  },
  { immediate: true },
);
</script>

<style scoped>
.page-shell {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 14px;
}

.fetching-state {
  padding: 12px 0;
}

.error-text {
  margin: 0;
  font-size: 14px;
  color: #b91c1c;
  font-weight: 600;
}

.info-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: #f8fbff;
  color: #475569;
}

.info-note-icon {
  color: #2563eb;
  flex-shrink: 0;
}

.info-note-text {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
}
</style>
